import { makeAutoObservable } from 'mobx'
import {
	EquipmentFilterValue,
	ExerciseParamFilter,
	ExerciseTypeFilterValue,
} from '../type/filters'

type AcceptedFilters = {
	includedMuscleCodes: string[]
	isAiAvailable: ExerciseParamFilter<boolean>
	exerciseType: ExerciseParamFilter<ExerciseTypeFilterValue>
	extraEquipments: ExerciseParamFilter<EquipmentFilterValue>
}

type FilterApplySource = 'advanced' | 'quick'

const uniqueCodes = (codes: string[]): string[] => Array.from(new Set(codes))

const normalizeSingleValueFilter = <T>(
	values: T[],
): ExerciseParamFilter<T> => {
	return values.length === 1 ? { isSet: true, value: values[0] } : { isSet: false }
}

export class ExerciseFilterStore {
	private includedMuscleCodes_: string[] = []
	private isAiAvailable_: ExerciseParamFilter<boolean> = { isSet: false }
	private selectedExerciseTypes_: ExerciseTypeFilterValue[] = []
	private selectedEquipmentTags_: EquipmentFilterValue[] = []
	private ftsSearchTerm_: string = ''
	private acceptedExerciseTypes_: ExerciseTypeFilterValue[] = []
	private acceptedEquipmentTags_: EquipmentFilterValue[] = []
	private acceptedApplySource_: FilterApplySource = 'quick'

	private acceptedFilters_: AcceptedFilters = {
		includedMuscleCodes: this.includedMuscleCodes_,
		isAiAvailable: this.isAiAvailable_,
		exerciseType: normalizeSingleValueFilter(this.selectedExerciseTypes_),
		extraEquipments: normalizeSingleValueFilter(this.selectedEquipmentTags_),
	}

	public constructor() {
		makeAutoObservable(this)
	}

	public setFtsSearchTerm = (term: string) => {
		this.ftsSearchTerm_ = term
	}

	public applyQuickMuscleFilter = (muscleCodes: string[]) => {
		this.includedMuscleCodes_ = uniqueCodes(muscleCodes)
		this.isAiAvailable_ = { isSet: false }
		this.selectedExerciseTypes_ = []
		this.selectedEquipmentTags_ = []
		this.confirmFilters('quick')
	}

	public toggleExerciseType = (value: ExerciseTypeFilterValue): void => {
		this.selectedExerciseTypes_ = this.selectedExerciseTypes_.includes(value)
			? this.selectedExerciseTypes_.filter((selected) => selected !== value)
			: [...this.selectedExerciseTypes_, value]
	}

	public toggleEquipmentTag = (value: EquipmentFilterValue): void => {
		this.selectedEquipmentTags_ = this.selectedEquipmentTags_.includes(value)
			? this.selectedEquipmentTags_.filter((selected) => selected !== value)
			: [...this.selectedEquipmentTags_, value]
	}

	public toggleIncludedMuscleCodes = (
		muscle: string,
		forceAddOrRemove?: boolean,
	) => {
		const isCurrentlyIncluded = this.includedMuscleCodes_.includes(muscle)

		if (forceAddOrRemove === true) {
			if (!isCurrentlyIncluded) {
				this.includedMuscleCodes_ = uniqueCodes([
					...this.includedMuscleCodes_,
					muscle,
				])
			}
		} else if (forceAddOrRemove === false) {
			if (isCurrentlyIncluded) {
				this.includedMuscleCodes_ = this.includedMuscleCodes_.filter(
					(m) => m !== muscle,
				)
			}
		} else {
			if (isCurrentlyIncluded) {
				this.includedMuscleCodes_ = this.includedMuscleCodes_.filter(
					(m) => m !== muscle,
				)
			} else {
				this.includedMuscleCodes_ = uniqueCodes([
					...this.includedMuscleCodes_,
					muscle,
				])
			}
		}
	}

	public toggleMuscleGroup = (muscleCodes: string[]): void => {
		const uniqueMuscleCodes = uniqueCodes(muscleCodes)
		const shouldRemoveGroup = uniqueMuscleCodes.every((muscleCode) =>
			this.includedMuscleCodes_.includes(muscleCode),
		)

		if (shouldRemoveGroup) {
			this.includedMuscleCodes_ = this.includedMuscleCodes_.filter(
				(muscleCode) => !uniqueMuscleCodes.includes(muscleCode),
			)
			return
		}

		this.includedMuscleCodes_ = uniqueCodes([
			...this.includedMuscleCodes_,
			...uniqueMuscleCodes,
		])
	}

	public changeIsAiAvailable = (
		value: 'Только с AI-тренером' | 'Только без AI-тренера' | string,
	) => {
		if (value === 'Только с AI-тренером') {
			this.isAiAvailable_ = { isSet: true, value: true }
		} else if (value === 'Только без AI-тренера') {
			this.isAiAvailable_ = { isSet: true, value: false }
		} else {
			this.isAiAvailable_ = { isSet: false }
		}
	}

	public changeExerciseType = (value: 'Кардио' | 'Силовые' | string): void => {
		if (value === 'Кардио') {
			this.selectedExerciseTypes_ = ['cardio']
		} else if (value === 'Силовые') {
			this.selectedExerciseTypes_ = ['strength']
		} else {
			this.selectedExerciseTypes_ = []
		}
	}

	public changeExtraEquipments = (
		value:
			| 'Без дополнительного оборудования'
			| 'С дополнительным оборудованием'
			| string,
	): void => {
		if (value === 'Без дополнительного оборудования') {
			this.selectedEquipmentTags_ = ['no_equipment']
		} else if (value === 'С дополнительным оборудованием') {
			this.selectedEquipmentTags_ = ['requires_equipment']
		} else {
			this.selectedEquipmentTags_ = []
		}
	}

	public confirmFilters = (source: FilterApplySource = 'advanced') => {
		this.acceptedExerciseTypes_ = [...this.selectedExerciseTypes_]
		this.acceptedEquipmentTags_ = [...this.selectedEquipmentTags_]
		this.acceptedApplySource_ = source
		this.acceptedFilters_ = {
			includedMuscleCodes: [...this.includedMuscleCodes_],
			isAiAvailable: { ...this.isAiAvailable_ },
			exerciseType: normalizeSingleValueFilter(this.selectedExerciseTypes_),
			extraEquipments: normalizeSingleValueFilter(this.selectedEquipmentTags_),
		}
	}

	public rollbackDraftToAccepted = () => {
		this.includedMuscleCodes_ = [...this.acceptedFilters_.includedMuscleCodes]
		this.isAiAvailable_ = { ...this.acceptedFilters_.isAiAvailable }
		this.selectedExerciseTypes_ = [...this.acceptedExerciseTypes_]
		this.selectedEquipmentTags_ = [...this.acceptedEquipmentTags_]
	}

	public resetDraft = () => {
		this.selectedExerciseTypes_ = []
		this.selectedEquipmentTags_ = []
		this.isAiAvailable_ = { isSet: false }
		this.includedMuscleCodes_ = []
	}

	public get filters() {
		return {
			includedMuscleCodes: this.includedMuscleCodes_,
			isAiAvailable: this.isAiAvailable_,
			exerciseType: normalizeSingleValueFilter(this.selectedExerciseTypes_),
			extraEquipments: normalizeSingleValueFilter(this.selectedEquipmentTags_),
		}
	}

	public get acceptedFilters() {
		return this.acceptedFilters_
	}

	public get includedMuscles() {
		return this.includedMuscleCodes_
	}

	public get selectedExerciseTypes() {
		return this.selectedExerciseTypes_
	}

	public get selectedEquipmentTags() {
		return this.selectedEquipmentTags_
	}

	public get draftSelectedCount() {
		return (
			this.includedMuscleCodes_.length +
			this.selectedExerciseTypes_.length +
			this.selectedEquipmentTags_.length
		)
	}

	public get acceptedSelectedCount() {
		return (
			this.acceptedFilters_.includedMuscleCodes.length +
			this.acceptedExerciseTypes_.length +
			this.acceptedEquipmentTags_.length
		)
	}

	public get hasAcceptedAdvancedFilters() {
		return this.acceptedApplySource_ === 'advanced' && this.acceptedSelectedCount > 0
	}

	public getFtsSearchTerm = () => {
		return this.ftsSearchTerm_
	}

	public resetState = () => {
		this.resetDraft()
		this.confirmFilters('quick')
	}
}
